import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'
import * as clientsService from '../../clientsService'
import { canManageClients, canEraseClient } from '../../featureRegistry'
import { isWorkspaceRole } from '../../../utils/workspacePermissions'
import type { WorkspaceRole } from '../../../types/workspace'

export const CLIENT_UPSERT_ACTION_ID = 'client.upsert'
export const CLIENT_ARCHIVE_ACTION_ID = 'client.archive'
export const CLIENT_RESTORE_ACTION_ID = 'client.restore'
export const CLIENT_ERASE_ACTION_ID = 'client.erase'

export interface ClientUpsertArgs {
  workspaceId?: string
  clientId?: string
  name: string
  email: string
  company?: string
  phone?: string
  notes?: string
}

export interface ClientArchiveArgs {
  workspaceId?: string
  clientId: string
}

export interface ClientRestoreArgs {
  workspaceId?: string
  clientId: string
}

export interface ClientEraseArgs {
  workspaceId?: string
  clientId: string
  confirmed?: boolean
}

const upsertArgsSchema: JSONSchemaType<ClientUpsertArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    clientId: { type: 'string', nullable: true },
    name: { type: 'string', minLength: 1, maxLength: 120 },
    email: { type: 'string', minLength: 3, maxLength: 254 },
    company: { type: 'string', nullable: true },
    phone: { type: 'string', nullable: true },
    notes: { type: 'string', nullable: true },
  },
  required: ['name', 'email'],
  additionalProperties: false,
}

const archiveArgsSchema: JSONSchemaType<ClientArchiveArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    clientId: { type: 'string', minLength: 1 },
  },
  required: ['clientId'],
  additionalProperties: false,
}

const restoreArgsSchema: JSONSchemaType<ClientRestoreArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    clientId: { type: 'string', minLength: 1 },
  },
  required: ['clientId'],
  additionalProperties: false,
}

const eraseArgsSchema: JSONSchemaType<ClientEraseArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    clientId: { type: 'string', minLength: 1 },
    confirmed: { type: 'boolean', nullable: true },
  },
  required: ['clientId'],
  additionalProperties: false,
}

const resolveWorkspace = (context: { workspaceId?: string; switchedOffModules?: ReadonlySet<string> }) => ({
  id: context.workspaceId ?? 'default-ws',
  plan: 'GROWTH_TIER',
  modules: {
    clientOutreachEnabled: !context.switchedOffModules?.has('clientOutreach'),
  },
} as unknown as import('../../../types/workspace').Workspace)

const formatActionError = (err: unknown): string => {
  if (err instanceof clientsService.ClientOutreachError) {
    return err.message.includes(err.code) ? err.message : `${err.code}: ${err.message}`
  }
  return err instanceof Error ? err.message : String(err)
}

const getActorRole = (context: { actor: { role?: unknown } }): WorkspaceRole | undefined =>
  isWorkspaceRole(context.actor.role) ? context.actor.role : undefined

const upsertClientAction: ActionDefinition<ClientUpsertArgs, { clientId: string }> = {
  id: CLIENT_UPSERT_ACTION_ID,
  label: 'Upsert client',
  description: 'Creates or updates a client contact in client outreach.',
  risk: 'LOW',
  domain: 'client',
  surfaces: ['catalog'],
  argsSchema: upsertArgsSchema,
  permission: (context) => canManageClients(resolveWorkspace(context), getActorRole(context)),
  run: async (args, context): Promise<ActionOutcome<{ clientId: string }>> => {
    const correlationId = context.correlationId ?? 'client-upsert-correlation'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for client operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await clientsService.upsertClient({
        workspaceId,
        clientId: args.clientId,
        name: args.name,
        email: args.email,
        company: args.company,
        phone: args.phone,
        notes: args.notes,
      })
      return {
        kind: 'COMPLETED',
        message: `Saved client ${args.name}.`,
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = formatActionError(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const archiveClientAction: ActionDefinition<ClientArchiveArgs, { clientId: string }> = {
  id: CLIENT_ARCHIVE_ACTION_ID,
  label: 'Archive client',
  description: 'Archives a client contact.',
  risk: 'MEDIUM',
  domain: 'client',
  surfaces: ['catalog'],
  argsSchema: archiveArgsSchema,
  permission: (context) => canManageClients(resolveWorkspace(context), getActorRole(context)),
  run: async (args, context): Promise<ActionOutcome<{ clientId: string }>> => {
    const correlationId = context.correlationId ?? 'client-archive-correlation'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for client operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await clientsService.archiveClient({
        workspaceId,
        clientId: args.clientId,
      })
      return {
        kind: 'COMPLETED',
        message: `Archived client ${args.clientId}.`,
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = formatActionError(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const restoreClientAction: ActionDefinition<ClientRestoreArgs, { clientId: string }> = {
  id: CLIENT_RESTORE_ACTION_ID,
  label: 'Restore client',
  description: 'Restores an archived client contact.',
  risk: 'LOW',
  domain: 'client',
  surfaces: ['catalog'],
  argsSchema: restoreArgsSchema,
  permission: (context) => canManageClients(resolveWorkspace(context), getActorRole(context)),
  run: async (args, context): Promise<ActionOutcome<{ clientId: string }>> => {
    const correlationId = context.correlationId ?? 'client-restore-correlation'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for client operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      const res = await clientsService.restoreClient({
        workspaceId,
        clientId: args.clientId,
      })
      return {
        kind: 'COMPLETED',
        message: `Restored client ${args.clientId}.`,
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = formatActionError(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

const eraseClientAction: ActionDefinition<ClientEraseArgs, { clientId: string; erased: boolean }> = {
  id: CLIENT_ERASE_ACTION_ID,
  label: 'Erase client',
  description: 'Permanently erases all personally identifiable information for a client.',
  risk: 'HIGH',
  domain: 'client',
  surfaces: ['catalog'],
  argsSchema: eraseArgsSchema,
  permission: (context) => canEraseClient(resolveWorkspace(context), getActorRole(context)),
  run: async (args, context): Promise<ActionOutcome<{ clientId: string; erased: boolean }>> => {
    const correlationId = context.correlationId ?? 'client-erase-correlation'
    const workspaceId = args.workspaceId || context.workspaceId
    if (!workspaceId) {
      const message = 'Workspace ID is required for client operations.'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }

    if (args.confirmed !== true) {
      return {
        kind: 'NEEDS_CONFIRMATION',
        message: 'Permanently erase client data? This cannot be undone.',
        confirmPayload: { clientId: args.clientId },
        correlationId,
      }
    }

    try {
      const res = await clientsService.eraseClient({
        workspaceId,
        clientId: args.clientId,
      })
      return {
        kind: 'COMPLETED',
        message: `Erased client ${args.clientId}.`,
        result: res,
        correlationId,
      }
    } catch (err) {
      const message = formatActionError(err)
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: true, correlationId }
    }
  },
}

export const registerClientActions = (): void => {
  if (!getAction(CLIENT_UPSERT_ACTION_ID)) registerAction(upsertClientAction)
  if (!getAction(CLIENT_ARCHIVE_ACTION_ID)) registerAction(archiveClientAction)
  if (!getAction(CLIENT_RESTORE_ACTION_ID)) registerAction(restoreClientAction)
  if (!getAction(CLIENT_ERASE_ACTION_ID)) registerAction(eraseClientAction)
}

registerClientActions()
